import { Card, Label } from '@repo/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { BoxPlotStats } from '../../../lib/stats/types';
import { DistributionGraph } from '../common/distribution-graph';
import { BoxPlot } from '../common/box-plot';

interface ExtendedBoxPlotStats extends BoxPlotStats {
  data: number[];
}

interface FiveNumberSummaryProps {
  visualizationData: ExtendedBoxPlotStats | null;
}

export function FiveNumberSummary({ visualizationData: result }: FiveNumberSummaryProps) {
  if (!result) {
    return (
      <ScrollableContainer>
        <div className="space-y-6">
          <Card className="p-4">
            <div className="text-center text-gray-500">
              Please select a data column to calculate the five number summary
            </div>
          </Card>
        </div>
      </ScrollableContainer>
    );
  }

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Five Number Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label>Minimum</Label>
              <div className="text-xl font-medium">{result.min.toFixed(4)}</div>
            </div>
            <div>
              <Label>First Quartile (Q1)</Label>
              <div className="text-xl font-medium">{result.q1.toFixed(4)}</div>
            </div>
            <div>
              <Label>Median (Q2)</Label>
              <div className="text-xl font-medium">{result.median.toFixed(4)}</div>
            </div>
            <div>
              <Label>Third Quartile (Q3)</Label>
              <div className="text-xl font-medium">{result.q3.toFixed(4)}</div>
            </div>
            <div>
              <Label>Maximum</Label>
              <div className="text-xl font-medium">{result.max.toFixed(4)}</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Box Plot</h3>
          <BoxPlot
            data={result}
            title="Five Number Summary"
            yAxisLabel="Value"
            height={300}
            className="w-full"
          />
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Distribution Visualization</h3>
          <DistributionGraph
            data={result.data}
            title="Distribution"
            xAxisLabel="Value"
            yAxisLabel="Frequency"
            height={300}
            labels={[
              {
                value: result.min,
                text: `Min: ${result.min.toFixed(2)}`,
                color: '#666666',
                lineStyle: 'solid'
              },
              {
                value: result.q1,
                text: `Q1: ${result.q1.toFixed(2)}`,
                color: '#4d4dff',
                lineStyle: 'dashed'
              },
              {
                value: result.median,
                text: `Median: ${result.median.toFixed(2)}`,
                color: '#ff4d4d',
                lineStyle: 'solid'
              },
              {
                value: result.q3,
                text: `Q3: ${result.q3.toFixed(2)}`,
                color: '#4d4dff',
                lineStyle: 'dashed'
              },
              {
                value: result.max,
                text: `Max: ${result.max.toFixed(2)}`,
                color: '#666666',
                lineStyle: 'solid'
              }
            ]}
            binCount={15}
            className="w-full"
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 